import { Form } from '../Form/Form'
import { ChooseRelatedKeywordsProps } from './Types'
import Styles from './SEOPlanningWizard.module.scss'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'almanac-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import Table from 'components/BlazeTable/BlazeTable'
import { Heading } from 'blaze-ui/Heading/Heading'
import { BasicKeywordRow, KeywordRow } from './KeywordRow'
import { KeywordsLoadingState } from './KeywordsLoadingState'
import { AddKeywordsInput } from './AddKeywordsInput'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export const ChooseRelatedKeywords = ({
  generateRelatedKeywordsMutation,
  selectedRelatedKeywords,
  primaryKeyword,
  setSelectedRelatedKeywords,
  onSubmit,
  goBack,
  addedKeywords,
  setAddedKeywords,
}: ChooseRelatedKeywordsProps) => {
  const { isMobile } = useResponsiveDesign()

  const toggleKeyword = (keyword: string) => {
    const newSelectedKeywords = new Set(selectedRelatedKeywords)
    if (newSelectedKeywords.has(keyword)) {
      newSelectedKeywords.delete(keyword)
    } else {
      newSelectedKeywords.add(keyword)
    }
    setSelectedRelatedKeywords(Array.from(newSelectedKeywords))
  }

  const addKeyword = (keyword: string) => {
    setAddedKeywords([...addedKeywords, keyword])
  }

  const relatedKeywordsResponse = generateRelatedKeywordsMutation.data
  const keywordItems = relatedKeywordsResponse?.tasks[0].result[0].items
  const keywords = keywordItems?.map((item) => item.keyword_data)
  const keywordsWithoutPrimary = keywords?.filter((keyword) => keyword.keyword !== primaryKeyword)
  const keywordsWithoutDuplicates = keywordsWithoutPrimary?.filter(
    (keyword, index, self) => self.findIndex((k) => k.keyword === keyword.keyword) === index,
  )
  const keywordsSorted = keywordsWithoutDuplicates?.sort(
    (a, b) => b.keyword_info.search_volume - a.keyword_info.search_volume,
  )
  const maxSearchVolume = keywordsSorted?.[0]?.keyword_info.search_volume || Infinity

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields>
            <div className={Styles.header}>
              <Heading level={4}>Select Secondary Keywords</Heading>
              <Text variant="secondary">Select 5 to 10 secondary keywords to enhance the reach of your content.</Text>
            </div>
            <Table variant="light">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Keyword</Table.Th>
                  <Table.Th maxWidth={isMobile ? '66px' : '148px'}>Volume</Table.Th>
                  <Table.Th maxWidth="66px">Difficulty</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {generateRelatedKeywordsMutation.isLoading && <KeywordsLoadingState isCheckbox />}
                {!!keywordsSorted && !!keywordsSorted.length ? (
                  keywordsSorted.map((item) => (
                    <KeywordRow
                      key={item.keyword}
                      item={item}
                      maxSearchVolume={maxSearchVolume}
                      isSelected={selectedRelatedKeywords.includes(item.keyword)}
                      toggleKeyword={toggleKeyword}
                      isCheckbox
                    />
                  ))
                ) : (
                  <>
                    {!generateRelatedKeywordsMutation.isLoading && !addedKeywords.length && (
                      <Table.Tr>
                        <Table.Td>
                          <Text variant="secondary">No related keywords found, try adding your own.</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </>
                )}
                {addedKeywords.map((keyword) => (
                  <BasicKeywordRow
                    key={keyword}
                    keyword={keyword}
                    isSelected={selectedRelatedKeywords.includes(keyword)}
                    toggleKeyword={toggleKeyword}
                    isCheckbox
                  />
                ))}
              </Table.Tbody>
            </Table>
            <AddKeywordsInput onAddKeyword={addKeyword} />
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>

      <Form.Actions isSticky>
        {!isMobile && <Form.PreviousButton onPress={goBack}>Choose Primary Keyword</Form.PreviousButton>}
        <Form.PrimaryActions>
          <Button size="lg" onPress={onSubmit} endIcon={ArrowRight} fullWidth={isMobile}>
            Generate Titles
          </Button>
          {isMobile && (
            <Button frontIcon={ArrowLeft} variant="secondary" size="lg" onPress={goBack} fullWidth={isMobile}>
              Choose Primary Keyword
            </Button>
          )}
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}
